<template>
  <div class="doto-document-prop">
    <input type="file" class="form-control" :name="field.name" :disabled="computedReadOnly" @change="onChange" />
    <div class="doto-document-prop row" v-for="(file, vkey) in value" :key="file.key">
      <div class="col-sm-4">
        <div
          class="ui thumbnail image"
          @mouseover="onMouseoverThumbnail(file.key)"
          @mouseleave="onMouseleaveThumbnail(file.key)"
        >
          <dimmer :dimmed="isThumbnailDimmed(file.key)" :inverted="false">
            <div class="content">
              <div class="center">
                <modal-opener
                  :disabled="computedReadOnly"
                  class="ui primary inverted button modal-opener"
                  :id="field.id"
                >
                  {{ $i18n.t('form_field_document-props_enlarge') }}
                </modal-opener>
                <button
                  :disabled="computedReadOnly"
                  type="button"
                  class="ui negative inverted button"
                  @click="onRemove(file.key)"
                >
                  {{ $i18n.t('form_field_document-props_remove') }}
                </button>
              </div>
            </div>
          </dimmer>

          <i class="fa fa-file"></i>
          <img
            :alt="file.name"
            class="ui image fluid"
            :src="file.url"
            onerror="this.parentNode.classList.add('image-error')"
          />
        </div>
        <modal :title="file.name" dialogClass="modal-lg" :id="field.id">
          <div class="doto-document-prop__gallery">
            <img class="ui image fluid" :alt="file.name" :src="file.url" />
          </div>
        </modal>
      </div>

      <div class="col-sm-8">
        <label class="control-label">{{ $i18n.t(file.title.label) }}</label>
        <input
          :disabled="computedReadOnly"
          class="form-control"
          :value="file.title.value"
          @input="setTitle(vkey, $event.target.value)"
          :max="file.title.max"
        />

        <label class="control-label">{{ $i18n.t(file.desc.label) }}</label>
        <froala-editor
          :key="starterKey"
          :computed-read-only="computedReadOnly"
          :value="file.desc.value"
          @change="setDesc(vkey, $event)"
        ></froala-editor>
      </div>
    </div>
  </div>
</template>

<script>
import { normalizeFields } from '../../../form'
import Dimmer from '../../../components/Dimmer'
import FroalaEditor from '../../../components/FroalaEditor'
import ModalOpener from '../../../components/modal/Opener'
import Modal from '../../../components/modal/Modal'
import formstore from '../../../utils/mixin-formstore'

/**
 * @class DocumentPropStore
 *
 * @description This component is the state handler/wrapper for DocumentProp
 */
export default {
  inheritAttrs: false,
  name: 'document-prop-store',
  mixins: [formstore],
  props: {
    path: {
      type: String,
    },
    pageId: {
      type: Number,
      default: null,
    },
    groupId: {
      type: Number,
      default: null,
    },
    rowId: {
      type: Number,
      default: null,
    },
    fieldId: {
      type: Number,
      default: null,
    },
    maxSize: {
      type: Number,
      default: null,
    },
    allowedTypes: {
      type: Array,
      default: null,
    },
    limit: {
      type: Number,
      default: null,
    },
    groupReadOnly: {
      type: Boolean,
      default: false,
    },
    uploadOperationId: {
      type: String,
      default: null,
    },
  },
  components: {
    Dimmer,
    Modal,
    ModalOpener,
    FroalaEditor,
  },
  data() {
    return {
      files: [],
      starterKey: 0,
      dimmed: null,
    }
  },
  methods: {
    setTitle(idx, value) {
      this.$commit('FORM_UPDATE_FIELD_DOCPROP_TITLE', {
        path: this.path,
        idx,
        value,
      })
    },
    setDesc(idx, $event) {
      const value = $event.value
      this.$commit('FORM_UPDATE_FIELD_DOCPROP_DESC', {
        path: this.path,
        idx,
        value,
      })
    },
    onChange($event) {
      Array.from($event.target.files).forEach(this.processFile)
    },
    onRemove(key) {
      this.files = this.files.filter((file) => file.key !== key)
      this.$el.querySelector(`input[name="${this.field.name}"]`).value = ''
    },
    onEnlarge(key) {},
    processFile(file) {
      const key = `${escape(file.name)}-${file.size}-${file.lastModified}`
      const reader = new FileReader()
      const addedFile = this.files.find((file) => file.key === key)
      if (this.files.includes(addedFile)) {
        // @todo log
        // console.log('file is already added with key', key)
        return
      }

      if (this.maxSize && file.size >= this.maxSize) {
        alert(this.$i18n.t('documentProp_file_sizeHigh'))
        return
      } else if (this.allowedTypes && !this.allowedTypes.includes(file.type)) {
        alert(this.$i18n.t('documentProp_file_notAllowedType'))
        return
      } else if (this.limit && this.files.length >= this.limit) {
        alert(this.$i18n.t('documentProp_file_reachedLimit'))
        return
      }

      if (!this.field.multiple) {
        this.files = []
      }

      this.files.push({
        name: escape(file.name),
        size: file.size,
        type: file.type,
        title: normalizeFields({
          type: 'text',
          label: this.$i18n.t('documentProp_metas_title'),
          formats: {
            maxlength: 300,
          },
        }),
        desc: normalizeFields({
          type: 'richtext',
          label: this.$i18n.t('documentProp_metas_description'),
          formats: {
            maxlength: 4000,
          },
        }),
        key,
      })

      reader.onload = (ev) => {
        let files = this.files.map(async (file) => {
          if (file.key === key) {
            file.url = ev.target.result
            if (this.uploadOperationId) {
              let { id, url } = await this.upload(file)
              file = { ...file, id, url }
            }
          }
          return file
        })
        Promise.all(files).then((files) => (this.files = files))
      }

      reader[this.uploadOperationId ? 'readAsBinaryString' : 'readAsDataURL'](file)
    },
    onMouseoverThumbnail(key) {
      this.dimmed = key
    },
    onMouseleaveThumbnail(key) {
      this.dimmed = null
    },
    isThumbnailDimmed(key) {
      return this.dimmed === key
    },
    async upload(file) {
      let parameters = {
        file: file.url,
        params: JSON.stringify({
          name: file.name,
          size: file.size,
          type: file.type,
        }),
      }
      return this.$client
        .execute({
          operationId: this.uploadOperationId,
          parameters,
          requestContentType: 'application/x-www-form-urlencoded',
        })
        .then((res) => res.body)
    },
  },
  computed: {
    computedReadOnly() {
      return this.readOnly || this.groupReadOnly
    },
    fileFiled() {
      return Object.assign({}, this.field, {
        type: 'file',
      })
    },
    titleField() {
      return normalizeFields({
        type: 'text',
        label: this.$i18n.t('form_field_document-props_meta_title'),
        name: `${this.field.name}[metas][NAME]`,
        formats: {
          maxlength: 300,
        },
      })
    },
    descField() {
      return normalizeFields({
        type: 'richtext',
        label: this.$i18n.t('form_field_document-props_meta_description'),
        name: `${this.field.name}[metas][DESC]`,
        formats: {
          maxlength: 4000,
        },
      })
    },
  },
  watch: {
    computedReadOnly: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.starterKey++
      }
    },
    files(newValue) {
      this.value = newValue
    },
  },
  mounted() {
    this.files = this.value instanceof Array ? [...this.value] : this.value ? [this.value] : []
  },
}
</script>

<style lang="scss">
.doto-document-prop {
  .fa-file {
    display: none;
  }
  .image-error {
    .fa-file {
      display: block;
      font-size: 300px;
    }
    .modal-opener,
    img {
      display: none !important;
    }
  }
  &__image {
    margin-bottom: 10px;
  }
  &__image-col {
    margin-bottom: 15px;
  }
  &__gallery {
    margin: -15px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
